import { NewsletterForm } from "./NewsletterForm";

const FOOTER_COLUMNS = [
  {
    title: "Platform",
    links: [
      { label: "C1 Identity Platform", href: "/platform-overview" },
      { label: "C1 Bridge", href: "/products/bridge" },
      { label: "C1 Connectors", href: "/integrations" },
      { label: "C1 MCP Servers", href: "/integrations/mcp-servers" },
      // VERIFY: "C1 SSO" / "C1 Application Authorization" are placeholder
      // names pending PMM confirmation (see PR description).
      { label: "C1 SSO", href: "/platform-overview#application-sign-in" },
      { label: "C1 Application Authorization", href: "/platform-overview#application-authorization" },
    ],
  },
  {
    title: "Products",
    links: [
      { label: "C1 Identity Governance", href: "https://www.c1.ai/products/iga" },
      { label: "C1 LLM Gateway", href: "/products/llm-gateway" },
      { label: "C1 MCP Gateway", href: "/products/mcp-gateway" },
      { label: "C1 Vault", href: "/products/vault" },
      { label: "C1 Agents", href: "/products/agents" },
      { label: "C1 AppHub", href: "/products/apphub" },
      { label: "C1 Egress", href: "/products/egress-proxy" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { label: "AI-Native Identity Governance", href: "/solutions/identity-governance" },
      { label: "User Access Review Automation", href: "/solutions/uar-automation" },
      { label: "JML Automation", href: "/solutions/jml-automation" },
      { label: "NHI & Identity Security", href: "/solutions/identity-security" },
      { label: "App & MCP Self Service", href: "/solutions/self-service-app-directory" },
      { label: "Deploy Custom Agents", href: "/solutions/deploy-custom-agents" },
      { label: "Agent SSO & Runtime Governance", href: "/solutions/agent-sso-runtime-governance" },
      { label: "Shadow AI Discovery", href: "/solutions/shadow-ai-discovery" },
      { label: "Secrets Management", href: "/solutions/secrets-management" },
      { label: "JIT Access", href: "/solutions/jit-access" },
    ],
  },
  {
    title: "Teams",
    links: [
      { label: "IT", href: "/solutions/teams/it" },
      { label: "Security", href: "/solutions/teams/security" },
      { label: "IAM Engineer", href: "/solutions/teams/iam-engineer" },
      { label: "Risk & Compliance", href: "/solutions/teams/risk-compliance" },
      { label: "AI Strategy & Enablement", href: "/solutions/teams/ai-strategy-enablement" },
      { label: "Small & Mid-Size Businesses", href: "/solutions/teams/small-mid-size-businesses" },
    ],
  },
  {
    title: "Customer Stories",
    links: [
      { label: "Ramp", href: "/customers/ramp" },
      { label: "DigitalOcean", href: "/customers/digitalocean" },
      { label: "Brex", href: "/customers/brex" },
      { label: "Instacart", href: "/customers/instacart" },
      { label: "Qualtrics", href: "/customers/qualtrics" },
      { label: "Zscaler", href: "/customers/zscaler" },
      { label: "PriceSmart", href: "/customers/pricesmart" },
      { label: "DailyPay", href: "/customers/dailypay" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blog", href: "/blog" },
      { label: "Guides", href: "/guides" },
      { label: "Talks", href: "/talks" },
      { label: "Glossary", href: "/glossary" },
      { label: "Authors", href: "/authors" },
      { label: "C1 Academy", href: "/c1-academy" },
      { label: "C1.ai Docs", href: "/docs/product/intro" },
      { label: "Connector Docs", href: "/docs/baton/intro" },
      { label: "Developer Docs", href: "/docs/developer/intro" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "C1 Transform", href: "/c1-transform-2026" },
      { label: "About Us", href: "/about-us" },
      { label: "News", href: "/news" },
      { label: "Security", href: "/security" },
      { label: "Partners", href: "/partners" },
      { label: "Press Kit", href: "/press-kit" },
      { label: "We're Hiring", href: "/careers" },
      { label: "Privacy Policy", href: "/privacy-policy" },
    ],
  },
] as const;

const SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/c1-ai",
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/channel/UCnIHlwBVjZEOYOcB05pOXNA",
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
] as const;

export function Footer() {
  return (
    <footer className="bg-[#141414] px-4 pb-16 pt-20 text-white md:px-8">
      <div className="mx-auto max-w-screen-xl">
        {/* Link columns. The labels are deliberately not headings: they repeat on every
            page and would otherwise nest under each page's last section in the
            heading outline. aria-labelledby keeps each list named for screen
            readers. */}
        <nav aria-label="Footer" className="pb-8">
          <ul className="grid gap-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
            {FOOTER_COLUMNS.map((col) => {
              const labelId = `footer-col-${col.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
              return (
                <li key={col.title}>
                  <p id={labelId} className="font-body text-sm font-semibold text-white">
                    {col.title}
                  </p>
                  <ul aria-labelledby={labelId} className="mt-3 space-y-3 leading-none">
                    {col.links.map((link) => (
                      <li key={link.label}>
                        <a
                          href={link.href}
                          className="font-body text-13 text-gray-60 underline-offset-2 hover:text-white hover:underline"
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Newsletter */}
        <div
          id="footer-newsletter"
          className="flex flex-col gap-6 border-b border-white/10 py-8 sm:flex-row sm:items-center sm:justify-between"
        >
          <NewsletterForm />
          <img
            src="/images/21972-312_SOC_NonCPA.png"
            alt="SOC 2 Type II Certified"
            className="w-10 shrink-0"
            loading="lazy"
          />
        </div>

        {/* Footer utility row */}
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 py-6 text-13 text-gray-60">
          <span>Copyright © 2026</span>

          <ul className="flex items-center justify-center gap-4">
            {SOCIAL_LINKS.map((s) => (
              <li key={s.label}>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-white transition-colors hover:text-gray-60"
                  aria-label={s.label}
                >
                  {s.icon}
                </a>
              </li>
            ))}
          </ul>

          <div className="flex justify-end">
            <img
              src="/images/c1-ai-logo-footer-gray.webp"
              alt="C1.ai"
              className="h-auto w-24 max-w-full"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </footer>
  );
}
